"use client";
import { Claude, DeepSeek, Gemini, Meta, Mistral, OpenAI, Perplexity } from "@lobehub/icons";
import { Card } from "@promptwatch/ui/components/card";
import { ChartContainer } from "@promptwatch/ui/components/chart";
import { TrendingUp } from "lucide-react";
import Image from "next/image";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis } from "recharts";
import type { ContentType } from "recharts/types/component/Label";

const chartData = [
  { model: "openai", value: 3290 },
  { model: "perplexity", value: 1800 },

  { model: "claude", value: 1500 },
  { model: "gemini", value: 1200 },
  { model: "deepseek", value: 1000 },
  { model: "meta", value: 923 },

  { model: "mistral", value: 800 },
];

const modelChartConfig = {
  openai: {
    label: "OpenAI",
    color: "#000",
    icon: OpenAI,
  },
  mistral: {
    label: "Mistral",
    color: "#FA520F",
    icon: Mistral,
  },
  claude: {
    label: "Claude",
    color: "#D97757",
    icon: Claude,
  },
  gemini: {
    label: "Gemini",
    color: "#538dfc",
    icon: Gemini.Color,
  },
  perplexity: {
    label: "Perplexity",
    color: "#22B8CD",
    icon: Perplexity,
  },
  meta: {
    label: "Meta",
    color: "#1D65C1",
    icon: Meta,
  },
  deepseek: {
    label: "DeepSeek",
    color: "#4D6BFE",
    icon: DeepSeek,
  },
};

const ProblemChart = () => {
  const renderCustomizedLabel = (props: { x: number; y: number; width: number; height: number; value: string }) => {
    const { x, y, width, height, value } = props;
    const config = modelChartConfig[value as keyof typeof modelChartConfig];
    const Icon = config.icon;

    return (
      <g>
        <foreignObject x={x + 8} y={y} width={width} height={height} className="text-white">
          <div className="flex items-center gap-2 pt-2.5 pl-2">
            <Icon className="size-4" />
            <span className="text-white">{config.label}</span>
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <Card>
      <div className="flex flex-col items-start gap-2 px-6 pt-6 text-sm">
        <p className="flex gap-2 font-medium leading-none text-base">
          Trending up by 28.5% this month <TrendingUp className="h-4 w-4" />
        </p>
        <div className="leading-none text-muted-foreground">Showing relative search traffic across major LLMs</div>
      </div>
      <div className="p-6 pt-0">
        <ChartContainer className="h-80 w-full" config={modelChartConfig}>
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              right: 16,
            }}
          >
            <CartesianGrid horizontal={false} vertical={true} />
            <YAxis dataKey="model" type="category" tickLine={false} tickMargin={10} axisLine={false} hide />
            <XAxis dataKey="value" type="number" hide />
            <Bar
              dataKey="value"
              layout="vertical"
              radius={5}
              fill="#8884d8"
              isAnimationActive={true}
              animationDuration={1000}
              animationEasing="ease-out"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={modelChartConfig[entry.model as keyof typeof modelChartConfig].color} />
              ))}
              <LabelList
                dataKey="model"
                position="insideLeft"
                offset={4}
                className="fill-[--color-label]"
                fontSize={12}
                content={renderCustomizedLabel as ContentType}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>
    </Card>
  );
};

const ProblemSection = () => {
  return (
    <section className="overflow-hidden py-16 lg:py-32" id="ai-search">
      <div className="mx-auto max-w-6xl px-12">
        <div className="grid gap-6 md:grid-cols-2 md:gap-12 lg:gap-24">
          <div className="relative z-10">
            <h2 className="text-title relative z-10 max-w-xl text-4xl pt-4 font-semibold">
              Search volumes are shifting from <span className="text-muted-foreground">search engines to AI</span>
            </h2>
            <div className="space-y-4 py-8">
              <p className="text-body">
                As users migrate from traditional search engines to AI platforms, traffic patterns are fundamentally changing.{" "}
                <span className="text-title font-medium">All major AI models are capturing search volume</span> — creating new discovery
                channels.
              </p>
              <p>
                Tracking this shift helps you understand where your audience is moving and how to optimize your presence across AI
                platforms.
              </p>

              <div className="pt-3">
                <blockquote className="border-l-4 pl-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    I Quit Google Search for AI—and I’m Not Going Back. Now ChatGPT, Perplexity, Claude—and even Google’s own AI—do it
                    better.
                  </p>

                  <div className="mt-2 space-y-3">
                    <cite className="block font-medium text-gray-500">- Joanna Stern</cite>
                    <Image
                      className="h-4.5 w-fit"
                      src="/wsj.png"
                      alt="Wallstreet journal Logo"
                      // height="20"
                      height={20}
                      width={200}
                      // width="auto"
                    />
                  </div>
                </blockquote>
              </div>
            </div>
          </div>
          <div className="relative mt-6 hidden sm:block">
            <div className="absolute -inset-20 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:24px_24px] sm:-inset-40" />
            <div className="absolute -inset-20 bg-gradient-to-b from-gray-50 via-transparent to-gray-50 sm:-inset-40" />
            <div className="absolute -inset-20 bg-gradient-to-r from-gray-50 via-transparent to-gray-50 sm:-inset-40" />
            <div className="tls-shadow-md relative overflow-hidden shadow-gray-950/[0.03]">
              <ProblemChart />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
